import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Save, RefreshCw, Eye, EyeOff, Layers, Image, Server,
  Users, ShoppingBag, Bell, Settings, CheckCircle2, AlertCircle, Plus, Trash2, X,
  LayoutDashboard, UserCog, Globe, Receipt, FileText, Sparkles, Search,
  GitCompareArrows, PanelBottom, ChevronUp, ChevronDown, ServerCog, Power, ShieldAlert, Send,
} from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useCMS } from "../context/CMSContext";
import {
  adminPatchCMS, adminToggleSection, adminUpdatePopup, adminResetCMS,
  adminGetPlatformBalance, adminGetPlatformTypes, adminGetOrders,
  adminUpload,
} from "../lib/api";
import DashboardPanel from "./admin/DashboardPanel";
import CustomersPanel from "./admin/CustomersPanel";
import HostingPanel from "./admin/HostingPanel";
import InvoicesPanel from "./admin/InvoicesPanel";
import PagesPanel from "./admin/PagesPanel";
import UsersPanel from "./admin/UsersPanel";
import InvoiceSettingsPanel from "./admin/InvoiceSettingsPanel";
import OperationsPanel from "./admin/OperationsPanel";
import BillingAutomationPanel from "./admin/BillingAutomationPanel";
import StackUserAuditPanel from "./admin/StackUserAuditPanel";
import PayLinksPanel from "./admin/PayLinksPanel";
import DynamicFooterPagesManager from "./admin/DynamicFooterPagesManager";

const SECTIONS = [
  { key: "promo", label: "Promo Bar" },
  { key: "hero", label: "Hero" },
  { key: "reviews", label: "Reviews" },
  { key: "wallOfLove", label: "Wall of Love (Videos)" },
  { key: "pricing", label: "Pricing" },
  { key: "club", label: "Mover Club" },
  { key: "features", label: "Features" },
  { key: "compare", label: "Compare Table" },
  { key: "switchCTA", label: "Switch CTA" },
  { key: "migration", label: "Migration" },
  { key: "faq", label: "FAQ" },
  { key: "footer", label: "Footer" },
];

function Tab({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
        active ? "bg-[#11151C] text-white" : "text-[#11151C]/70 hover:bg-[#FFE6D6]/40"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  const isErr = type === "error";
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 ${
      isErr ? "bg-red-600 text-white" : "bg-[#11151C] text-white"
    }`}>
      {isErr ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-[#FF6B3D]" />}
      <span className="text-sm">{msg}</span>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", textarea = false }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">{label}</span>
      <Comp
        type={type}
        value={value || ""}
        rows={textarea ? 3 : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
      />
    </label>
  );
}

function UploadImageField({ label, value, onChange, showToast }) {
  return (
    <div className="mt-3">
      <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">{label}</span>
      <div className="mt-1 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="https://... or upload below"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
        />
        <label className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[#FF6B3D]/40 bg-[#FFFBF6] text-sm font-semibold text-[#B23B12] cursor-pointer hover:bg-[#FFE6D6]">
          <Image className="w-4 h-4" />
          <span>Upload</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const res = await adminUpload(f);
                const backend = process.env.REACT_APP_BACKEND_URL || "";
                const fullUrl = `${backend}${res.url}`;
                onChange(fullUrl);
                showToast?.("Uploaded");
              } catch (err) {
                showToast?.(err?.response?.data?.detail || "Upload failed", "error");
              }
              e.target.value = "";
            }}
          />
        </label>
      </div>
      {value && (
        <div className="mt-2 relative inline-block">
          <img src={value} alt="" className="max-h-24 rounded-lg border border-[#11151C]/10" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/95 border border-[#11151C]/10 flex items-center justify-center hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3 text-red-500" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user, signOut, loading } = useAuth();
  const { cms, reload } = useCMS();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [draft, setDraft] = useState(null);
  const [toast, setToast] = useState({ msg: "", type: "" });
  const [twentyiBalance, setTwentyiBalance] = useState(null);
  const [twentyiTypes, setTwentyiTypes] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (cms) setDraft(JSON.parse(JSON.stringify(cms)));
  }, [cms]);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 2800);
  };

  const handleSave = async (key) => {
    try {
      const patch = { [key]: draft[key] };
      await adminPatchCMS(patch);
      await reload();
      showToast("Saved");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Save failed", "error");
    }
  };

  const handleToggleSection = async (key, val) => {
    try {
      await adminToggleSection(key, val);
      setDraft({ ...draft, sectionsEnabled: { ...(draft.sectionsEnabled || {}), [key]: val } });
      await reload();
      showToast(`${key} ${val ? "enabled" : "disabled"}`);
    } catch (e) {
      showToast("Toggle failed", "error");
    }
  };

  const savePopup = async () => {
    try {
      await adminUpdatePopup(draft.popup);
      await reload();
      showToast("Popup saved");
    } catch (e) {
      showToast("Save failed", "error");
    }
  };

  const loadPlatform = async () => {
    let errMsg = null;
    try {
      const b = await adminGetPlatformBalance();
      setTwentyiBalance(b?.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      errMsg = `Balance fetch failed (${status || "no-response"}): ${detail}`;
    }
    try {
      const t = await adminGetPlatformTypes();
      setTwentyiTypes(t?.types || []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      errMsg = (errMsg ? errMsg + " · " : "") + `Plan types fetch failed (${status || "no-response"}): ${detail}`;
    }
    if (errMsg) showToast(errMsg, "error");
  };

  const loadOrders = async () => {
    try { setOrders(await adminGetOrders()); } catch (e) {}
  };

  useEffect(() => {
    if (tab === "twentyi") loadPlatform();
    if (tab === "orders") loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!draft || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="lm-font min-h-screen bg-[#FAF8F4]">
      {/* Top bar */}
      <div className="bg-white border-b border-[#11151C]/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={32} />
            <span className="lm-font font-extrabold text-[#11151C]">Live Mover · Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="https://my.20i.com/services/support"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-20i-support"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#11151C]/70 hover:text-[#FF6B3D]"
              title="Open 20i support tickets"
            >
              <Server className="w-4 h-4" /> 20i Support ↗
            </a>
            <Link to="/" target="_blank" className="text-sm font-semibold text-[#11151C] hover:text-[#FF6B3D]">
              View site ↗
            </Link>
            <button
              onClick={() => { signOut(); navigate("/login"); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#11151C] hover:text-red-600"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[260px_1fr] gap-6">
        <aside className="space-y-1.5">
          <Tab icon={LayoutDashboard} label="Dashboard" active={tab === "dashboard"} onClick={() => setTab("dashboard")} />
          <Tab icon={UserCog} label="Customers" active={tab === "customers"} onClick={() => setTab("customers")} />
          <Tab icon={Globe} label="Hosting Accounts" active={tab === "hosting"} onClick={() => setTab("hosting")} />
          <Tab icon={Receipt} label="Invoices" active={tab === "invoices"} onClick={() => setTab("invoices")} />
          <Tab icon={Receipt} label="Invoice Settings" active={tab === "invoice_settings"} onClick={() => setTab("invoice_settings")} />
          <Tab icon={ServerCog} label="Operations" active={tab === "operations"} onClick={() => setTab("operations")} />
          <Tab icon={Power} label="Billing Automation" active={tab === "billing_auto"} onClick={() => setTab("billing_auto")} />
          <Tab icon={ShieldAlert} label="StackUser Audit" active={tab === "stackuser_audit"} onClick={() => setTab("stackuser_audit")} />
          <Tab icon={Send} label="Pay Links" active={tab === "pay_links"} onClick={() => setTab("pay_links")} />
          <Tab icon={FileText} label="Pages" active={tab === "pages"} onClick={() => setTab("pages")} />
          <div className="my-2 h-px bg-[#11151C]/10" />
          <Tab icon={Layers} label="Sections" active={tab === "sections"} onClick={() => setTab("sections")} />
          <Tab icon={Settings} label="Site Settings" active={tab === "site"} onClick={() => setTab("site")} />
          <Tab icon={Search} label="SEO & Analytics" active={tab === "seo"} onClick={() => setTab("seo")} />
          <Tab icon={Image} label="Logo & Hero Image" active={tab === "logo"} onClick={() => setTab("logo")} />
          <Tab icon={Eye} label="Hero Text" active={tab === "hero"} onClick={() => setTab("hero")} />
          <Tab icon={Eye} label="Promo Bar" active={tab === "promo"} onClick={() => setTab("promo")} />
          <Tab icon={Eye} label="Plans (Pricing)" active={tab === "plans"} onClick={() => setTab("plans")} />
          <Tab icon={Sparkles} label="Features" active={tab === "features"} onClick={() => setTab("features")} />
          <Tab icon={GitCompareArrows} label="Compare Table" active={tab === "compare"} onClick={() => setTab("compare")} />
          <Tab icon={Eye} label="FAQs" active={tab === "faqs"} onClick={() => setTab("faqs")} />
          <Tab icon={PanelBottom} label="Footer" active={tab === "footer"} onClick={() => setTab("footer")} />
          <Tab icon={Bell} label="Popup" active={tab === "popup"} onClick={() => setTab("popup")} />
          <div className="my-2 h-px bg-[#11151C]/10" />
          <Tab icon={Server} label="Platform" active={tab === "twentyi"} onClick={() => setTab("twentyi")} />
          <Tab icon={ShoppingBag} label="Checkout Orders" active={tab === "orders"} onClick={() => setTab("orders")} />
          <Tab icon={Users} label="Site Users" active={tab === "users"} onClick={() => setTab("users")} />
        </aside>

        <main className="bg-white rounded-2xl border border-[#11151C]/10 p-6 min-h-[60vh]">
          {tab === "dashboard" && <DashboardPanel />}
          {tab === "customers" && <CustomersPanel onToast={showToast} />}
          {tab === "hosting" && <HostingPanel onToast={showToast} />}
          {tab === "invoices" && <InvoicesPanel onToast={showToast} />}
          {tab === "invoice_settings" && <InvoiceSettingsPanel onToast={showToast} />}
          {tab === "operations" && <OperationsPanel onToast={showToast} />}
          {tab === "billing_auto" && <BillingAutomationPanel onToast={showToast} />}
          {tab === "stackuser_audit" && <StackUserAuditPanel onToast={showToast} />}
          {tab === "pay_links" && <PayLinksPanel onToast={showToast} />}
          {tab === "pages" && <PagesPanel onToast={showToast} />}

          {tab === "seo" && (
            <div className="space-y-4 max-w-2xl">
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">SEO & Analytics</h2>
              <p className="text-[#11151C]/60 text-sm">Default meta tags for the homepage and social sharing. Individual pages override these.</p>
              <TextField label="Page Title" value={draft.seo?.title}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), title: v } })} />
              <TextField label="Meta Description" textarea value={draft.seo?.description}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), description: v } })} />
              <TextField label="Keywords (comma separated)" value={draft.seo?.keywords}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), keywords: v } })} />
              <div className="grid sm:grid-cols-2 gap-3">
                <TextField label="Open Graph Title" value={draft.seo?.ogTitle}
                  onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), ogTitle: v } })} />
                <TextField label="Twitter Handle" value={draft.seo?.twitterHandle}
                  onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), twitterHandle: v } })} />
              </div>
              <TextField label="Open Graph Description" textarea value={draft.seo?.ogDescription}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), ogDescription: v } })} />
              <UploadImageField
                label="Open Graph image (social share)"
                value={draft.seo?.ogImage}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), ogImage: v } })}
                showToast={showToast}
              />
              <TextField label="Google Analytics ID (G-XXXXXX or UA-XXXX)" value={draft.seo?.googleAnalyticsId}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), googleAnalyticsId: v } })} />

              <div className="border border-[#11151C]/10 rounded-xl p-4 bg-[#FFFBF6] space-y-3">
                <div>
                  <h3 className="font-bold text-[#11151C] text-sm">Marketing pixels</h3>
                  <p className="text-[11px] text-[#11151C]/55 mt-0.5">
                    Pasted IDs are injected on every page (no code edits required). Leave blank to disable.
                  </p>
                </div>
                <TextField
                  label="Google Tag Manager ID (GTM-XXXXXXX)"
                  value={draft.seo?.gtmId}
                  onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), gtmId: v } })}
                  data-testid="seo-gtm-id"
                />
                <TextField
                  label="Facebook Pixel ID (15-16 digit number)"
                  value={draft.seo?.facebookPixelId}
                  onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), facebookPixelId: v } })}
                  data-testid="seo-fb-pixel-id"
                />
                <p className="text-[11px] text-[#11151C]/55">
                  GTM also injects a &lt;noscript&gt; iframe inside &lt;body&gt;. Facebook Pixel fires a default
                  <code className="px-1">PageView</code> event on every route change. Verify with:
                  Chrome DevTools → Network → filter <code className="px-1">fbevents.js</code> or
                  <code className="px-1">gtm.js</code>.
                </p>
              </div>

              <div className="border border-[#11151C]/10 rounded-xl p-4 bg-[#FFFBF6] space-y-3">
                <div>
                  <h3 className="font-bold text-[#11151C] text-sm">Custom code injection</h3>
                  <p className="text-[11px] text-[#11151C]/55 mt-0.5">
                    Paste raw HTML — <code>&lt;script&gt;</code>, <code>&lt;link&gt;</code>,
                    <code>&lt;meta&gt;</code>, <code>&lt;noscript&gt;</code>, inline CSS etc. — and it will be
                    injected on <b>every page</b>. Header goes into <code>&lt;head&gt;</code>, Footer goes just
                    before <code>&lt;/body&gt;</code>. Live-updates without a rebuild.
                  </p>
                </div>
                <label className="block text-xs">
                  <span className="font-bold uppercase tracking-wider text-[#11151C]/70">Header code (injected in &lt;head&gt;)</span>
                  <textarea
                    value={draft.seo?.customHeaderCode || ""}
                    onChange={(e) => setDraft({ ...draft, seo: { ...(draft.seo || {}), customHeaderCode: e.target.value } })}
                    rows={6}
                    placeholder={"<!-- e.g. -->\n<script src=\"https://embed.tawk.to/xxxxx/1abc\"></script>\n<meta name=\"verify-hotjar\" content=\"abc123\" />"}
                    data-testid="seo-header-code"
                    className="mt-1 w-full font-mono text-[11px] leading-relaxed border border-[#11151C]/15 rounded-lg px-3 py-2 bg-white"
                  />
                </label>
                <label className="block text-xs">
                  <span className="font-bold uppercase tracking-wider text-[#11151C]/70">Footer code (injected before &lt;/body&gt;)</span>
                  <textarea
                    value={draft.seo?.customFooterCode || ""}
                    onChange={(e) => setDraft({ ...draft, seo: { ...(draft.seo || {}), customFooterCode: e.target.value } })}
                    rows={6}
                    placeholder={"<!-- e.g. Crisp / Intercom / Hotjar loader / retargeting pixels -->\n<script>window.$crisp=[];window.CRISP_WEBSITE_ID=\"xxxxx\";(function(){var d=document,s=d.createElement(\"script\");s.src=\"https://client.crisp.chat/l.js\";s.async=1;d.getElementsByTagName(\"head\")[0].appendChild(s);})();</script>"}
                    data-testid="seo-footer-code"
                    className="mt-1 w-full font-mono text-[11px] leading-relaxed border border-[#11151C]/15 rounded-lg px-3 py-2 bg-white"
                  />
                </label>
                <p className="text-[11px] text-[#11151C]/55">
                  ⚠️ Anything you paste here runs on your visitors&apos; browsers. Only paste snippets from services
                  you trust (Google, Meta, Tawk, Crisp, Hotjar, Intercom, etc.). Malicious code can steal sessions.
                </p>
              </div>

              <TextField label="Robots meta" value={draft.seo?.robots}
                onChange={(v) => setDraft({ ...draft, seo: { ...(draft.seo || {}), robots: v } })} />
              <button onClick={() => handleSave("seo")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                <Save className="w-4 h-4" /> Save SEO
              </button>
            </div>
          )}

          {tab === "logo" && (
            <div className="space-y-6 max-w-2xl">
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Logo & Hero Image</h2>
              <div>
                <h3 className="font-bold text-[#11151C] mb-2">Site logo</h3>
                <p className="text-[#11151C]/60 text-sm mb-3">Upload a transparent PNG/SVG. If empty, the default Live Mover mark is used.</p>
                <UploadImageField
                  label="Logo image"
                  value={draft.site?.logoUrl}
                  onChange={(v) => setDraft({ ...draft, site: { ...draft.site, logoUrl: v } })}
                  showToast={showToast}
                />
                <UploadImageField
                  label="Favicon"
                  value={draft.site?.faviconUrl}
                  onChange={(v) => setDraft({ ...draft, site: { ...draft.site, faviconUrl: v } })}
                  showToast={showToast}
                />
                <button onClick={() => handleSave("site")} className="lm-btn-primary mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                  <Save className="w-4 h-4" /> Save logo
                </button>
              </div>

              <div className="pt-6 border-t border-[#11151C]/10">
                <h3 className="font-bold text-[#11151C] mb-2">Hero illustration</h3>
                <p className="text-[#11151C]/60 text-sm mb-3">Upload a custom image to replace the default dashboard mockup. Recommended aspect 4:5.</p>
                <label className="inline-flex items-center gap-2 mb-3">
                  <input type="checkbox" checked={!!draft.heroImage?.useDefault}
                    onChange={(e) => setDraft({ ...draft, heroImage: { ...(draft.heroImage || {}), useDefault: e.target.checked } })} />
                  <span className="text-sm font-semibold">Use default illustration</span>
                </label>
                <UploadImageField
                  label="Custom hero image"
                  value={draft.heroImage?.imageUrl}
                  onChange={(v) => setDraft({ ...draft, heroImage: { ...(draft.heroImage || {}), imageUrl: v } })}
                  showToast={showToast}
                />
                <button onClick={() => handleSave("heroImage")} className="lm-btn-primary mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                  <Save className="w-4 h-4" /> Save Hero Image
                </button>
              </div>
            </div>
          )}

          {tab === "features" && (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Features</h2>
                <div className="flex gap-2">
                  <button onClick={() => setDraft({ ...draft, features: [...(draft.features || []), { icon: "Zap", title: "New feature", desc: "Description" }] })}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B3D]">
                    <Plus className="w-4 h-4" /> Add Feature
                  </button>
                  <button onClick={() => handleSave("features")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                    <Save className="w-4 h-4" /> Save
                  </button>
                </div>
              </div>
              <p className="text-[#11151C]/60 text-sm mb-4">Icon names use <a href="https://lucide.dev/icons" target="_blank" rel="noopener noreferrer" className="text-[#FF6B3D] font-semibold">Lucide icon names</a> (e.g. Zap, ShieldCheck, Database, BarChart3).</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {(draft.features || []).map((f, idx) => (
                  <div key={idx} className="border border-[#11151C]/10 rounded-2xl p-4">
                    <div className="grid grid-cols-3 gap-2">
                      <TextField label="Icon" value={f.icon}
                        onChange={(v) => { const a=[...draft.features]; a[idx]={...f, icon:v}; setDraft({...draft, features:a}); }} />
                      <div className="col-span-2">
                        <TextField label="Title" value={f.title}
                          onChange={(v) => { const a=[...draft.features]; a[idx]={...f, title:v}; setDraft({...draft, features:a}); }} />
                      </div>
                    </div>
                    <div className="mt-2">
                      <TextField label="Description" value={f.desc}
                        onChange={(v) => { const a=[...draft.features]; a[idx]={...f, desc:v}; setDraft({...draft, features:a}); }} />
                    </div>
                    <div className="mt-2 flex justify-between items-center">
                      <div className="flex gap-1">
                        <button disabled={idx===0} onClick={() => { const a=[...draft.features]; [a[idx-1],a[idx]]=[a[idx],a[idx-1]]; setDraft({...draft, features:a}); }}
                          className="p-1.5 hover:bg-[#FFE6D6] rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                        <button disabled={idx===draft.features.length-1} onClick={() => { const a=[...draft.features]; [a[idx+1],a[idx]]=[a[idx],a[idx+1]]; setDraft({...draft, features:a}); }}
                          className="p-1.5 hover:bg-[#FFE6D6] rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                      </div>
                      <button onClick={() => setDraft({ ...draft, features: draft.features.filter((_, i) => i !== idx) })}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "compare" && (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Compare Table</h2>
                <button onClick={() => handleSave("compare")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                  <Save className="w-4 h-4" /> Save All
                </button>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-[#11151C]">Providers (column headers)</h3>
                    <button onClick={() => { const h=[...(draft.compare?.headers||[]), { name:"New", price:"\u20b9999/mo" }]; setDraft({...draft, compare:{...(draft.compare||{}), headers:h, rows:(draft.compare?.rows||[]).map(r=>({...r, values:[...(r.values||[]), ""]})), additional:(draft.compare?.additional||[]).map(r=>({...r, values:[...(r.values||[]), false]})) }}); }}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B3D]"><Plus className="w-4 h-4" /> Add column</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {(draft.compare?.headers || []).map((h, idx) => (
                      <div key={idx} className="border border-[#11151C]/10 rounded-xl p-3">
                        <div className="grid grid-cols-2 gap-2">
                          <TextField label="Name" value={h.name}
                            onChange={(v) => { const arr=[...draft.compare.headers]; arr[idx]={...h, name:v}; setDraft({...draft, compare:{...draft.compare, headers:arr}}); }} />
                          <TextField label="Price" value={h.price}
                            onChange={(v) => { const arr=[...draft.compare.headers]; arr[idx]={...h, price:v}; setDraft({...draft, compare:{...draft.compare, headers:arr}}); }} />
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <label className="inline-flex items-center gap-1.5 text-xs">
                            <input type="checkbox" checked={!!h.highlight}
                              onChange={(e) => { const arr=[...draft.compare.headers]; arr[idx]={...h, highlight:e.target.checked}; setDraft({...draft, compare:{...draft.compare, headers:arr}}); }} />
                            <span className="font-semibold">Highlight (our brand)</span>
                          </label>
                          <button onClick={() => { const i=idx; setDraft({...draft, compare:{...draft.compare, headers: draft.compare.headers.filter((_,k)=>k!==i), rows: draft.compare.rows.map(r=>({...r, values:r.values.filter((_,k)=>k!==i)})), additional: draft.compare.additional.map(r=>({...r, values:r.values.filter((_,k)=>k!==i)})) }}); }}
                            className="text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-[#11151C]">Resource Rows</h3>
                    <button onClick={() => { const newRow={ feature:"New row", values:(draft.compare?.headers||[]).map(()=>"") }; setDraft({...draft, compare:{...(draft.compare||{}), rows:[...(draft.compare?.rows||[]), newRow]}}); }}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B3D]"><Plus className="w-4 h-4" /> Add row</button>
                  </div>
                  <div className="space-y-2">
                    {(draft.compare?.rows || []).map((r, idx) => (
                      <div key={idx} className="border border-[#11151C]/10 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <input value={r.feature} placeholder="Feature name"
                            onChange={(e) => { const arr=[...draft.compare.rows]; arr[idx]={...r, feature:e.target.value}; setDraft({...draft, compare:{...draft.compare, rows:arr}}); }}
                            className="flex-1 border border-[#11151C]/15 rounded-lg px-2 py-1 text-sm font-semibold" />
                          <button onClick={() => setDraft({...draft, compare:{...draft.compare, rows: draft.compare.rows.filter((_,k)=>k!==idx)}})}
                            className="text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                          {(draft.compare?.headers || []).map((h, ci) => (
                            <input key={ci} value={r.values?.[ci] || ""} placeholder={h.name}
                              onChange={(e) => { const arr=[...draft.compare.rows]; const vals=[...(r.values||[])]; vals[ci]=e.target.value; arr[idx]={...r, values:vals}; setDraft({...draft, compare:{...draft.compare, rows:arr}}); }}
                              className="border border-[#11151C]/15 rounded-lg px-2 py-1 text-xs" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-[#11151C]">Feature Rows (checkboxes)</h3>
                    <button onClick={() => { const newRow={ feature:"New feature", values:(draft.compare?.headers||[]).map(()=>false) }; setDraft({...draft, compare:{...(draft.compare||{}), additional:[...(draft.compare?.additional||[]), newRow]}}); }}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B3D]"><Plus className="w-4 h-4" /> Add feature</button>
                  </div>
                  <div className="space-y-2">
                    {(draft.compare?.additional || []).map((r, idx) => (
                      <div key={idx} className="border border-[#11151C]/10 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <input value={r.feature} placeholder="Feature name"
                            onChange={(e) => { const arr=[...draft.compare.additional]; arr[idx]={...r, feature:e.target.value}; setDraft({...draft, compare:{...draft.compare, additional:arr}}); }}
                            className="flex-1 border border-[#11151C]/15 rounded-lg px-2 py-1 text-sm font-semibold" />
                          <button onClick={() => setDraft({...draft, compare:{...draft.compare, additional: draft.compare.additional.filter((_,k)=>k!==idx)}})}
                            className="text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                          {(draft.compare?.headers || []).map((h, ci) => (
                            <label key={ci} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[#11151C]/10 rounded-lg">
                              <input type="checkbox" checked={!!r.values?.[ci]}
                                onChange={(e) => { const arr=[...draft.compare.additional]; const vals=[...(r.values||[])]; vals[ci]=e.target.checked; arr[idx]={...r, values:vals}; setDraft({...draft, compare:{...draft.compare, additional:arr}}); }} />
                              <span>{h.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "footer" && (
            <div className="space-y-4 max-w-3xl">
              <div className="flex items-center justify-between">
                <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Footer</h2>
                <button onClick={() => handleSave("footer")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
              <TextField label="Footer tagline" textarea value={draft.footer?.tagline}
                onChange={(v) => setDraft({ ...draft, footer: { ...(draft.footer || {}), tagline: v } })} />

              <div className="grid sm:grid-cols-2 gap-3 border border-[#11151C]/10 rounded-xl p-4 bg-[#FFFBF6]">
                <TextField
                  label="WhatsApp number (international format, e.g. +91XXXXXXXXXX)"
                  value={draft.footer?.whatsapp_number}
                  onChange={(v) => setDraft({ ...draft, footer: { ...(draft.footer || {}), whatsapp_number: v } })}
                  data-testid="footer-whatsapp-number"
                />
                <TextField
                  label="Support ticket URL (e.g. https://my.livemover.com/tickets)"
                  value={draft.footer?.support_ticket_url}
                  onChange={(v) => setDraft({ ...draft, footer: { ...(draft.footer || {}), support_ticket_url: v } })}
                  data-testid="footer-support-ticket-url"
                />
                <label className="sm:col-span-2 inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={!!draft.footer?.show_email}
                    onChange={(e) => setDraft({ ...draft, footer: { ...(draft.footer || {}), show_email: e.target.checked } })}
                    data-testid="footer-show-email"
                    className="accent-[#FF6B3D]"
                  />
                  <span className="text-[#11151C]">
                    Show <b>support email</b> as a third card in the &quot;Instant Support&quot; row
                    <span className="text-[#11151C]/55 text-xs"> (uses Site &rarr; supportEmail)</span>
                  </span>
                </label>
                <p className="text-[11px] text-[#11151C]/55 sm:col-span-2">
                  WhatsApp + Ticket appear as professional cards inside an &quot;Instant Support&quot; block under the brand. Floating WhatsApp button also shows on every page when a number is set.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-[#11151C] mb-2">Social URLs</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {["twitter","facebook","instagram","linkedin"].map((k) => (
                    <TextField key={k} label={k} value={draft.footer?.socials?.[k]}
                      onChange={(v) => setDraft({ ...draft, footer: { ...(draft.footer || {}), socials: { ...(draft.footer?.socials || {}), [k]: v } } })} />
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-[#11151C] mb-2">Static link columns</h3>
                <p className="text-xs text-[#11151C]/55 mb-3">
                  Pages marked &quot;Show in footer&quot; auto-append below these. Duplicate labels are removed automatically (case-insensitive). Only the <b>first 3 columns</b> are shown on the live site — reorder by deleting and re-adding to change which 3 ship. Use URLs like <code>/contact</code>, <code>#pricing</code> or full <code>https://...</code>.
                </p>
                <div className="space-y-3">
                  {(draft.footer?.columns || []).map((c, idx) => {
                    const linksArr = (c.links || []).map((l) => typeof l === "string" ? { label: l, href: "" } : { label: l.label || "", href: l.href || "" });
                    const updateLinks = (newLinks) => {
                      const a = [...(draft.footer?.columns || [])];
                      a[idx] = { ...c, links: newLinks };
                      setDraft({ ...draft, footer: { ...(draft.footer || {}), columns: a } });
                    };
                    return (
                      <div key={idx} className="border border-[#11151C]/10 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3 gap-2">
                          <input value={c.title}
                            onChange={(e) => { const a=[...draft.footer.columns]; a[idx]={...c, title:e.target.value}; setDraft({...draft, footer:{...draft.footer, columns:a}}); }}
                            placeholder="Column title"
                            data-testid={`footer-col-${idx}-title`}
                            className="font-semibold border border-[#11151C]/15 rounded-lg px-2 py-1.5 text-sm flex-1" />
                          <button onClick={() => setDraft({...draft, footer:{...draft.footer, columns: draft.footer.columns.filter((_,k)=>k!==idx)}})}
                            className="text-red-500 p-1.5 hover:bg-red-50 rounded" title="Remove column"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="space-y-1.5">
                          {linksArr.map((l, li) => (
                            <div key={li} className="flex items-center gap-1.5">
                              <input value={l.label}
                                onChange={(e) => { const a=[...linksArr]; a[li]={...l, label:e.target.value}; updateLinks(a); }}
                                placeholder="Label"
                                data-testid={`footer-link-${idx}-${li}-label`}
                                className="flex-1 border border-[#11151C]/15 rounded-md px-2 py-1.5 text-sm" />
                              <input value={l.href}
                                onChange={(e) => { const a=[...linksArr]; a[li]={...l, href:e.target.value}; updateLinks(a); }}
                                placeholder="/path, #anchor, https://..."
                                data-testid={`footer-link-${idx}-${li}-href`}
                                className="flex-1 border border-[#11151C]/15 rounded-md px-2 py-1.5 text-sm" />
                              <button onClick={() => updateLinks(linksArr.filter((_,k)=>k!==li))}
                                className="text-red-400 p-1.5 hover:bg-red-50 rounded" title="Remove link"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                          <button onClick={() => updateLinks([...linksArr, { label: "New link", href: "" }])}
                            data-testid={`footer-col-${idx}-add-link`}
                            className="text-xs font-semibold text-[#FF6B3D] inline-flex items-center gap-1 mt-1"><Plus className="w-3.5 h-3.5" /> Add link</button>
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={() => setDraft({...draft, footer:{...(draft.footer || {}), columns:[...(draft.footer?.columns||[]), { title:"New Column", links:[] }]}})}
                    data-testid="footer-add-column"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B3D]"><Plus className="w-4 h-4" /> Add column</button>
                </div>
              </div>

              <DynamicFooterPagesManager onToast={showToast} />
            </div>
          )}

          {tab === "sections" && (
            <div>
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Section Visibility</h2>
              <p className="text-[#11151C]/60 text-sm mt-1">Show or hide entire sections on the public site.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {SECTIONS.map((s) => {
                  const enabled = (draft.sectionsEnabled || {})[s.key] !== false;
                  return (
                    <button
                      key={s.key}
                      onClick={() => handleToggleSection(s.key, !enabled)}
                      className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                        enabled ? "border-[#FF6B3D]/40 bg-[#FFFBF6]" : "border-[#11151C]/15 bg-white"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        {enabled ? <Eye className="w-4 h-4 text-[#FF6B3D]" /> : <EyeOff className="w-4 h-4 text-[#11151C]/40" />}
                        <span className="font-semibold text-[#11151C] text-sm">{s.label}</span>
                      </span>
                      <span className={`text-xs font-bold ${enabled ? "text-[#B23B12]" : "text-[#11151C]/40"}`}>
                        {enabled ? "VISIBLE" : "HIDDEN"}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8 pt-6 border-t border-[#11151C]/10">
                <button
                  onClick={async () => {
                    if (!window.confirm("Reset ALL CMS content to defaults? This cannot be undone.")) return;
                    await adminResetCMS();
                    await reload();
                    showToast("Reset to defaults");
                  }}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 hover:underline"
                >
                  <RefreshCw className="w-4 h-4" /> Reset all content to defaults
                </button>
              </div>
            </div>
          )}

          {tab === "site" && (
            <div className="space-y-4 max-w-2xl">
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Site Settings</h2>
              <TextField label="Brand Name" value={draft.site?.brandName}
                onChange={(v) => setDraft({ ...draft, site: { ...draft.site, brandName: v } })} />
              <TextField label="Support Email" value={draft.site?.supportEmail}
                onChange={(v) => setDraft({ ...draft, site: { ...draft.site, supportEmail: v } })} />
              <TextField label="Currency Symbol" value={draft.site?.currencySymbol}
                onChange={(v) => setDraft({ ...draft, site: { ...draft.site, currencySymbol: v } })} />
              <button onClick={() => handleSave("site")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          )}

          {tab === "hero" && (
            <div className="space-y-4 max-w-2xl">
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Hero Section</h2>
              {["line1", "line2", "line3", "brandHighlight", "description", "ctaText", "ctaUrl", "guaranteeText"].map((k) => (
                <TextField key={k} label={k} value={draft.hero?.[k]}
                  onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, [k]: v } })}
                  textarea={k === "description"} />
              ))}
              <button onClick={() => handleSave("hero")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          )}

          {tab === "promo" && (
            <div className="space-y-4 max-w-2xl">
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Promo Bar</h2>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!draft.promo?.enabled}
                  onChange={(e) => setDraft({ ...draft, promo: { ...draft.promo, enabled: e.target.checked } })} />
                <span className="text-sm font-semibold">Enabled</span>
              </label>
              {["label", "text", "oldPrice", "ctaText", "ctaUrl"].map((k) => (
                <TextField key={k} label={k} value={draft.promo?.[k]}
                  onChange={(v) => setDraft({ ...draft, promo: { ...draft.promo, [k]: v } })} />
              ))}
              <TextField label="Timer Minutes" type="number" value={draft.promo?.timerMinutes}
                onChange={(v) => setDraft({ ...draft, promo: { ...draft.promo, timerMinutes: Number(v) } })} />
              <button onClick={() => handleSave("promo")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          )}

          {tab === "plans" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Pricing Plans</h2>
                <button onClick={() => handleSave("plans")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                  <Save className="w-4 h-4" /> Save all
                </button>
              </div>

              {/* Section headings shown above the pricing cards on the homepage */}
              <div className="border border-[#11151C]/10 rounded-2xl p-5 bg-[#FFFBF6]">
                <h3 className="text-sm font-bold text-[#11151C] mb-3">Section headings (homepage)</h3>
                <div className="grid sm:grid-cols-3 gap-3">
                  <TextField
                    label="Eyebrow (small uppercase chip)"
                    value={draft.pricing_section?.eyebrow ?? "Pricing & Plans"}
                    onChange={(v) => setDraft({ ...draft, pricing_section: { ...(draft.pricing_section || {}), eyebrow: v } })}
                    data-testid="pricing-section-eyebrow"
                  />
                  <TextField
                    label="Title"
                    value={draft.pricing_section?.title ?? "Simple, Transparent Pricing"}
                    onChange={(v) => setDraft({ ...draft, pricing_section: { ...(draft.pricing_section || {}), title: v } })}
                    data-testid="pricing-section-title"
                  />
                  <TextField
                    label="Subtitle"
                    value={draft.pricing_section?.subtitle ?? "All plans include 30-day money back guarantee"}
                    onChange={(v) => setDraft({ ...draft, pricing_section: { ...(draft.pricing_section || {}), subtitle: v } })}
                    data-testid="pricing-section-subtitle"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {(draft.plans || []).map((p, idx) => (
                  <div key={p.key || idx} className="border border-[#11151C]/10 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-[#11151C]">{p.name}</h3>
                      <label className="inline-flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={!!p.popular}
                          onChange={(e) => {
                            const plans = [...draft.plans];
                            plans[idx] = { ...p, popular: e.target.checked };
                            setDraft({ ...draft, plans });
                          }} />
                        <span className="font-semibold">Mark as Most Popular</span>
                      </label>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {["name", "tagline", "oldPrice", "price", "renewal_price", "period", "renew", "ctaText", "ctaUrl", "twentyiTypeId"].map((k) => (
                        <TextField key={k} label={k === "renewal_price" ? "renewal_price (₹/yr)" : k} value={p[k]}
                          onChange={(v) => {
                            const plans = [...draft.plans];
                            plans[idx] = { ...p, [k]: k === "twentyiTypeId" ? Number(v) : (k === "renewal_price" ? (v === "" ? "" : Number(v)) : v) };
                            setDraft({ ...draft, plans });
                          }} />
                      ))}
                    </div>

                    <div className="mt-5 border-t border-[#11151C]/10 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-[#11151C]">Pricing tiers (multi-year)</h4>
                        <button
                          onClick={() => {
                            const plans = [...draft.plans];
                            const tiers = [...(p.pricing_tiers || [])];
                            tiers.push({ months: 12, price: 0, twentyiTypeId: p.twentyiTypeId || 0 });
                            plans[idx] = { ...p, pricing_tiers: tiers };
                            setDraft({ ...draft, plans });
                          }}
                          data-testid={`plan-add-tier-${p.key}`}
                          className="text-xs font-semibold text-[#FF6B3D] inline-flex items-center gap-1"
                        ><Plus className="w-3.5 h-3.5" /> Add tier</button>
                      </div>
                      <p className="text-[11px] text-[#11151C]/55 mb-3">
                        Each tier maps a duration (months) to a price (INR) and a 20i package type id. Customers pick a tier at checkout. Leave empty to support 1-year only.
                      </p>
                      <div className="space-y-2">
                        {(p.pricing_tiers || []).map((t, ti) => (
                          <div key={ti} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`plan-tier-row-${p.key}-${ti}`}>
                            <label className="text-xs">
                              <span className="text-[10px] uppercase font-bold text-[#11151C]/60">Months</span>
                              <input type="number" value={t.months}
                                onChange={(e) => {
                                  const plans = [...draft.plans];
                                  const tiers = [...(p.pricing_tiers || [])];
                                  tiers[ti] = { ...t, months: Number(e.target.value) };
                                  plans[idx] = { ...p, pricing_tiers: tiers };
                                  setDraft({ ...draft, plans });
                                }}
                                className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-2 py-1.5 text-sm" />
                            </label>
                            <label className="text-xs">
                              <span className="text-[10px] uppercase font-bold text-[#11151C]/60">Price (₹)</span>
                              <input type="number" value={t.price}
                                onChange={(e) => {
                                  const plans = [...draft.plans];
                                  const tiers = [...(p.pricing_tiers || [])];
                                  tiers[ti] = { ...t, price: Number(e.target.value) };
                                  plans[idx] = { ...p, pricing_tiers: tiers };
                                  setDraft({ ...draft, plans });
                                }}
                                className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-2 py-1.5 text-sm" />
                            </label>
                            <label className="text-xs">
                              <span className="text-[10px] uppercase font-bold text-[#11151C]/60">Renewal ₹</span>
                              <input type="number" value={t.renewal_price ?? ""} placeholder="(same as price)"
                                onChange={(e) => {
                                  const plans = [...draft.plans];
                                  const tiers = [...(p.pricing_tiers || [])];
                                  const val = e.target.value;
                                  tiers[ti] = { ...t, renewal_price: val === "" ? "" : Number(val) };
                                  plans[idx] = { ...p, pricing_tiers: tiers };
                                  setDraft({ ...draft, plans });
                                }}
                                data-testid={`plan-tier-renewal-${p.key}-${ti}`}
                                className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-2 py-1.5 text-sm" />
                            </label>
                            <label className="text-xs">
                              <span className="text-[10px] uppercase font-bold text-[#11151C]/60">20i type id</span>
                              <input type="number" value={t.twentyiTypeId || ""}
                                onChange={(e) => {
                                  const plans = [...draft.plans];
                                  const tiers = [...(p.pricing_tiers || [])];
                                  tiers[ti] = { ...t, twentyiTypeId: Number(e.target.value) };
                                  plans[idx] = { ...p, pricing_tiers: tiers };
                                  setDraft({ ...draft, plans });
                                }}
                                className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-2 py-1.5 text-sm" />
                            </label>
                            <button
                              onClick={() => {
                                const plans = [...draft.plans];
                                plans[idx] = { ...p, pricing_tiers: (p.pricing_tiers || []).filter((_, i) => i !== ti) };
                                setDraft({ ...draft, plans });
                              }}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            ><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Feature checklist (the ✓ Single Website / 50 GB SSD / etc. list) */}
                    <div className="mt-5 border-t border-[#11151C]/10 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-[#11151C]">Feature checklist (shown on pricing card)</h4>
                        <button
                          onClick={() => {
                            const plans = [...draft.plans];
                            const feats = [...(p.features || []), "New feature"];
                            plans[idx] = { ...p, features: feats };
                            setDraft({ ...draft, plans });
                          }}
                          data-testid={`plan-add-feature-${p.key}`}
                          className="text-xs font-semibold text-[#FF6B3D] inline-flex items-center gap-1"
                        ><Plus className="w-3.5 h-3.5" /> Add feature</button>
                      </div>
                      <p className="text-[11px] text-[#11151C]/55 mb-3">
                        Drag the ↑↓ buttons to reorder. These appear with green check icons under each plan card.
                      </p>
                      <div className="space-y-1.5">
                        {(p.features || []).map((f, fi) => (
                          <div key={fi} className="flex items-center gap-2" data-testid={`plan-feature-row-${p.key}-${fi}`}>
                            <span className="w-5 text-[11px] text-[#11151C]/45 text-right">{fi + 1}.</span>
                            <input
                              value={f}
                              onChange={(e) => {
                                const plans = [...draft.plans];
                                const feats = [...(p.features || [])];
                                feats[fi] = e.target.value;
                                plans[idx] = { ...p, features: feats };
                                setDraft({ ...draft, plans });
                              }}
                              data-testid={`plan-feature-input-${p.key}-${fi}`}
                              className="flex-1 border border-[#11151C]/15 rounded-lg px-2.5 py-1.5 text-sm"
                            />
                            <button
                              disabled={fi === 0}
                              onClick={() => {
                                const plans = [...draft.plans];
                                const feats = [...(p.features || [])];
                                [feats[fi - 1], feats[fi]] = [feats[fi], feats[fi - 1]];
                                plans[idx] = { ...p, features: feats };
                                setDraft({ ...draft, plans });
                              }}
                              className="p-1.5 text-[#11151C]/50 hover:bg-[#FFE6D6] rounded-lg disabled:opacity-30"
                            ><ChevronUp className="w-4 h-4" /></button>
                            <button
                              disabled={fi === (p.features || []).length - 1}
                              onClick={() => {
                                const plans = [...draft.plans];
                                const feats = [...(p.features || [])];
                                [feats[fi + 1], feats[fi]] = [feats[fi], feats[fi + 1]];
                                plans[idx] = { ...p, features: feats };
                                setDraft({ ...draft, plans });
                              }}
                              className="p-1.5 text-[#11151C]/50 hover:bg-[#FFE6D6] rounded-lg disabled:opacity-30"
                            ><ChevronDown className="w-4 h-4" /></button>
                            <button
                              onClick={() => {
                                const plans = [...draft.plans];
                                plans[idx] = { ...p, features: (p.features || []).filter((_, i) => i !== fi) };
                                setDraft({ ...draft, plans });
                              }}
                              data-testid={`plan-feature-delete-${p.key}-${fi}`}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            ><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        {!(p.features || []).length && (
                          <p className="text-xs text-[#11151C]/45 italic px-2">No features yet — add the first one above.</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "faqs" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">FAQs</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDraft({ ...draft, faqs: [...(draft.faqs || []), { q: "New question", a: "Answer here" }] })}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B3D]"
                  >
                    <Plus className="w-4 h-4" /> Add FAQ
                  </button>
                  <button onClick={() => handleSave("faqs")} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                    <Save className="w-4 h-4" /> Save
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {(draft.faqs || []).map((f, idx) => (
                  <div key={idx} className="border border-[#11151C]/10 rounded-xl p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-[#FF6B3D] pt-2">{String(idx + 1).padStart(2, "0")}</span>
                      <div className="flex-1 space-y-2">
                        <input value={f.q}
                          onChange={(e) => {
                            const faqs = [...draft.faqs];
                            faqs[idx] = { ...f, q: e.target.value };
                            setDraft({ ...draft, faqs });
                          }}
                          className="w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm font-semibold" />
                        <textarea rows={2} value={f.a}
                          onChange={(e) => {
                            const faqs = [...draft.faqs];
                            faqs[idx] = { ...f, a: e.target.value };
                            setDraft({ ...draft, faqs });
                          }}
                          className="w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <button onClick={() => setDraft({ ...draft, faqs: draft.faqs.filter((_, i) => i !== idx) })}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "popup" && (
            <div className="space-y-4 max-w-2xl">
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Popup / Offer</h2>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!draft.popup?.enabled}
                  onChange={(e) => setDraft({ ...draft, popup: { ...(draft.popup || {}), enabled: e.target.checked } })} />
                <span className="text-sm font-semibold">Enabled (show on site)</span>
              </label>
              <TextField label="Title" value={draft.popup?.title}
                onChange={(v) => setDraft({ ...draft, popup: { ...(draft.popup || {}), title: v } })} />
              <TextField label="Description" textarea value={draft.popup?.description}
                onChange={(v) => setDraft({ ...draft, popup: { ...(draft.popup || {}), description: v } })} />

              <div>
                <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">
                  Image (upload or paste URL)
                </span>
                <div className="mt-1 flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    placeholder="https://... or upload below"
                    value={draft.popup?.image_url || ""}
                    onChange={(e) => setDraft({ ...draft, popup: { ...(draft.popup || {}), image_url: e.target.value } })}
                    className="flex-1 border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                  />
                  <label className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[#FF6B3D]/40 bg-[#FFFBF6] text-sm font-semibold text-[#B23B12] cursor-pointer hover:bg-[#FFE6D6]">
                    <Image className="w-4 h-4" />
                    <span>Upload image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const res = await adminUpload(f);
                          const backend = process.env.REACT_APP_BACKEND_URL || "";
                          const fullUrl = `${backend}${res.url}`;
                          setDraft({ ...draft, popup: { ...(draft.popup || {}), image_url: fullUrl } });
                          showToast("Image uploaded");
                        } catch (err) {
                          showToast(err?.response?.data?.detail || "Upload failed", "error");
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
                {draft.popup?.image_url && (
                  <div className="mt-3 relative inline-block">
                    <img
                      src={draft.popup.image_url}
                      alt="Popup preview"
                      className="max-h-40 rounded-xl border border-[#11151C]/10"
                    />
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, popup: { ...(draft.popup || {}), image_url: "" } })}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 border border-[#11151C]/10 flex items-center justify-center hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-[#11151C]/50 mt-1.5">Max 5MB. JPG/PNG/WebP recommended.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <TextField label="CTA Text" value={draft.popup?.cta_text}
                  onChange={(v) => setDraft({ ...draft, popup: { ...(draft.popup || {}), cta_text: v } })} />
                <TextField label="CTA URL" value={draft.popup?.cta_url}
                  onChange={(v) => setDraft({ ...draft, popup: { ...(draft.popup || {}), cta_url: v } })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Trigger</span>
                  <select value={draft.popup?.trigger || "delay"}
                    onChange={(e) => setDraft({ ...draft, popup: { ...(draft.popup || {}), trigger: e.target.value } })}
                    className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm">
                    <option value="load">On page load</option>
                    <option value="delay">After delay</option>
                    <option value="exit">On exit intent</option>
                  </select>
                </label>
                <TextField label="Delay (seconds)" type="number" value={draft.popup?.delay_seconds}
                  onChange={(v) => setDraft({ ...draft, popup: { ...(draft.popup || {}), delay_seconds: Number(v) } })} />
              </div>
              <button onClick={savePopup} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
                <Save className="w-4 h-4" /> Save Popup
              </button>
            </div>
          )}

          {tab === "twentyi" && (
            <div>
              <div className="flex items-center justify-between">
                <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Hosting Platform</h2>
                <button onClick={loadPlatform} className="inline-flex items-center gap-1.5 text-sm font-semibold">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div className="border border-[#11151C]/10 rounded-2xl p-5">
                  <div className="text-xs text-[#11151C]/60 uppercase font-bold tracking-wider">Account Balance</div>
                  <div className="mt-2 lm-font font-extrabold text-3xl text-[#11151C]">
                    £{twentyiBalance?.balance ?? "-"}
                  </div>
                </div>
                <div className="border border-[#11151C]/10 rounded-2xl p-5">
                  <div className="text-xs text-[#11151C]/60 uppercase font-bold tracking-wider">Package Types</div>
                  <div className="mt-2 lm-font font-extrabold text-3xl text-[#11151C]">{twentyiTypes.length}</div>
                </div>
              </div>
              <h3 className="mt-8 font-bold text-[#11151C]">Available Package Types (use the IDs in Plans tab)</h3>
              <div className="mt-3 border border-[#11151C]/10 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFFBF6]"><tr><th className="text-left p-3">ID</th><th className="text-left p-3">Label</th><th className="text-left p-3">Platform</th></tr></thead>
                  <tbody>
                    {twentyiTypes.map((t) => (
                      <tr key={t.id} className="border-t border-[#11151C]/5">
                        <td className="p-3 font-mono">{t.id}</td>
                        <td className="p-3 font-semibold">{t.label}</td>
                        <td className="p-3 text-[#11151C]/60">{t.platform}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "orders" && (
            <div>
              <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Orders</h2>
              <div className="mt-4 border border-[#11151C]/10 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFFBF6]">
                    <tr>
                      <th className="text-left p-3">Date</th><th className="text-left p-3">Customer</th>
                      <th className="text-left p-3">Plan</th><th className="text-left p-3">Domain</th>
                      <th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-[#11151C]/50">No orders yet.</td></tr>
                    )}
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-[#11151C]/5">
                        <td className="p-3 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                        <td className="p-3">{o.customer_name}<br /><span className="text-xs text-[#11151C]/50">{o.customer_email}</span></td>
                        <td className="p-3 font-semibold">{o.plan_name}</td>
                        <td className="p-3">{o.domain_name}</td>
                        <td className="p-3">₹{o.amount_inr}</td>
                        <td className="p-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${
                            o.status === "paid" ? "bg-green-100 text-green-700" :
                            o.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"
                          }`}>{o.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "users" && <UsersPanel onToast={showToast} />}
        </main>
      </div>

      <Toast msg={toast.msg} type={toast.type} />
    </div>
  );
}
